import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { api } from '../../lib/api';
import { getToken } from '../../lib/auth';

interface User {
  role: 'admin' | 'teacher' | 'student' | 'parent' | 'secretariat';
  is_platform_admin?: boolean;
}

interface SidebarItem {
  label: string;
  path: string;
  icon: string;
  description?: string;
  badge?: string | number;
}

interface DashboardSidebarProps {
  user: User;
  isCollapsed?: boolean;
  onNavigate?: (path: string) => void;
}

const DashboardSidebar = ({ user, isCollapsed = false, onNavigate }: DashboardSidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState<string[]>(['main']);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState<number>(0);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState<number>(0);
  const [unreadContactSubmissionsCount, setUnreadContactSubmissionsCount] = useState<number>(0);

  const refreshUnread = async () => {
    const token = getToken();
    if (!token) {
      setUnreadMessagesCount(0);
      setUnreadNotificationsCount(0);
      setUnreadContactSubmissionsCount(0);
      return;
    }
    try {
      const resp = await api.get<{ total: number }>('/api/users/me/unread-count');
      const total = Number((resp.data as any)?.total ?? 0);
      setUnreadMessagesCount(Number.isFinite(total) ? total : 0);
    } catch {
      // ignore
    }
    try {
      const resp2 = await api.get<{ unread: number }>('/api/notificari/notifications/unread-count');
      const unread = Number((resp2.data as any)?.unread ?? 0);
      setUnreadNotificationsCount(Number.isFinite(unread) ? unread : 0);
    } catch {
      // ignore
    }
    if (user.role === 'admin' && user.is_platform_admin) {
      try {
        const resp3 = await api.get<{ total: number }>('/api/admin/platform/contact-submissions', {
          params: { unread_only: true, page: 1, page_size: 1 },
        });
        const total = Number((resp3.data as any)?.total ?? 0);
        setUnreadContactSubmissionsCount(Number.isFinite(total) ? total : 0);
      } catch {
        // ignore
      }
    } else {
      setUnreadContactSubmissionsCount(0);
    }
  };

  useEffect(() => {
    void refreshUnread();
    const t = window.setInterval(() => void refreshUnread(), 15000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void refreshUnread();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname, location.search]);

  const sidebarItems: Record<string, SidebarItem[]> = {
    teacher: [
      {
        label: 'Prezentare',
        path: '/teacher-dashboard',
        icon: 'LayoutDashboard',
        description: 'Panoul profesorului',
      },
      {
        label: 'Clasele mele',
        path: '/class-management-hub',
        icon: 'School',
        description: 'Gestionare clase',
      },
      {
        label: 'Catalog',
        path: '/gradebook-interface',
        icon: 'BookOpen',
        description: 'Note și evaluări',
      },
      {
        label: 'Teme',
        path: '/teme',
        icon: 'FileText',
        description: 'Creează și evaluează',
      },
      {
        label: 'Clasă online',
        path: '/online-classroom',
        icon: 'Video',
        description: 'Sesiuni virtuale',
      },
      {
        label: 'Calendar',
        path: '/calendar',
        icon: 'CalendarDays',
        description: 'Programări & reminder',
      },
      {
        label: 'Notificări',
        path: '/notifications',
        icon: 'Bell',
        description: 'Alarme & activitate',
      },
      {
        label: 'Mesaje',
        path: '/communication-center',
        icon: 'MessageSquare',
        description: 'Comunicare',
      },
    ],
    admin: [
      {
        label: 'Prezentare',
        path: '/platform-admin-dashboard',
        icon: 'LayoutDashboard',
        description: 'Panou admin',
      },
      {
        label: 'Notificări',
        path: '/notifications',
        icon: 'Bell',
        description: 'Alerte & activitate',
      },
      {
        label: 'Școli',
        path: '/platform-admin-dashboard?tab=schools',
        icon: 'Building2',
        description: 'Gestionare școli',
      },
      {
        label: 'Utilizatori',
        path: '/platform-admin-dashboard?tab=users',
        icon: 'Users',
        description: 'Administrare utilizatori',
      },
      {
        label: 'Mesaje',
        path: '/communication-center',
        icon: 'MessageSquare',
        description: 'Comunicare',
      },
      {
        label: 'Contact',
        path: '/platform-admin-dashboard?tab=contact',
        icon: 'Mail',
        description: 'Mesaje din Contact',
      },
      {
        label: 'Clase',
        path: '/platform-admin-dashboard?tab=classes',
        icon: 'School',
        description: 'Gestionare clase',
      },
      {
        label: 'Calendar',
        path: '/calendar',
        icon: 'CalendarDays',
        description: 'Programări & reminder',
      },
      {
        label: 'Rapoarte',
        path: '/platform-admin-dashboard?tab=reports',
        icon: 'BarChart3',
        description: 'Analiză',
      },
      {
        label: 'Setări',
        path: '/platform-admin-dashboard?tab=settings',
        icon: 'Settings',
        description: 'Configurare sistem',
      },
      {
        label: 'Audit',
        path: '/platform-admin-dashboard?tab=audit',
        icon: 'ShieldCheck',
        description: 'Istoric modificari',
      },
    ],
    secretariat: [
      { label: 'Prezentare', path: '/secretariat-dashboard', icon: 'LayoutDashboard', description: 'Panou secretariat' },
      { label: 'Utilizatori', path: '/secretariat-dashboard?tab=users', icon: 'Users', description: 'Profesori / elevi / părinți' },
      { label: 'Clase', path: '/secretariat-dashboard?tab=classes', icon: 'School', description: 'Clase sincronizate' },
      { label: 'Arhivă', path: '/secretariat-dashboard?tab=archive', icon: 'Archive', description: 'Cataloage arhivate' },
      { label: 'Cereri aderare', path: '/secretariat-dashboard?tab=requests', icon: 'UserCheck', description: 'Aprobări profesori' },
      { label: 'Elevi & dosare', path: '/secretariat-dashboard?tab=students', icon: 'FolderOpen', description: 'Dosar digital' },
      { label: 'Transferuri', path: '/secretariat-dashboard?tab=transfers', icon: 'ArrowLeftRight', description: 'Transfer elevi' },
      { label: 'Foi Google', path: '/secretariat-dashboard?tab=sheets', icon: 'FileSpreadsheet', description: 'Linkuri Google Sheets' },
      { label: 'Registratură', path: '/secretariat-dashboard?tab=registry', icon: 'ClipboardList', description: 'Cereri & numere' },
      { label: 'Rapoarte', path: '/secretariat-dashboard?tab=reports', icon: 'BarChart3', description: 'Exporturi oficiale' },
      { label: 'Notificări', path: '/notifications', icon: 'Bell', description: 'Alerte & activitate' },
    ],
  };

  const currentItems = (sidebarItems[user.role] || []).filter((item) => {
    if (item.label === 'Catalog' || item.label === 'Teme') return false;
    if (user.role === 'admin' && user.is_platform_admin && item.path === '/class-management-hub') return false;
    if (user.role === 'admin' && user.is_platform_admin && item.label === 'Clase') return false;
    if (user.role === 'admin' && !user.is_platform_admin && item.label === 'Școli') return false;
    if (user.role === 'admin' && !user.is_platform_admin && item.path === '/platform-admin-dashboard?tab=contact') return false;
    return true;
  });

  const handleNavigation = (path: string) => {
    if (onNavigate) {
      onNavigate(path);
    } else {
      navigate(path);
    }
    setIsMobileOpen(false);
  };

  const isActivePath = (path: string) => {
    const [basePath, query] = path.split('?');
    const currentBasePath = location.pathname;
    if (currentBasePath !== basePath) return false;

    // If an item has query params (e.g. platform dashboard tabs), require them to match too.
    if (!query) return !location.search || location.search === '';
    if (!location.search) return false;

    const want = new URLSearchParams(query);
    const have = new URLSearchParams(location.search.startsWith('?') ? location.search.slice(1) : location.search);
    for (const [k, v] of want.entries()) {
      if ((have.get(k) || '') !== v) return false;
    }
    return true;
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) =>
      prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section]
    );
  };

  if (user.role !== 'teacher' && user.role !== 'admin' && user.role !== 'secretariat') {
    return null;
  }

  return (
    <>
      {!isMobileOpen ? (
        <button
          type="button"
          className="fixed top-[4.5rem] right-3 z-[880] lg:hidden h-10 w-10 rounded-md bg-card border border-border shadow-sm flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          onClick={() => setIsMobileOpen(true)}
          aria-label="Deschide meniul"
        >
          <Icon name="Menu" size={20} />
        </button>
      ) : null}

      <aside
        id="dashboard-sidebar"
        className={`fixed left-0 top-16 bottom-0 z-[900] bg-card border-r border-border transition-all duration-300 ease-out lg:fixed ${
          isCollapsed ? 'w-16' : 'w-64'
        } hidden lg:block overflow-y-auto`}
        aria-label="Navigare bară laterală (panou)"
      >
        <div className="p-3 border-b border-border flex items-center justify-between">
          <div className="text-sm font-semibold text-foreground">Meniu</div>
          <button
            type="button"
            className="h-9 w-9 inline-flex items-center justify-center rounded-md hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary"
            onClick={() => setIsMobileOpen(false)}
            aria-label="Închide meniul"
          >
            <Icon name="X" size={18} />
          </button>
        </div>
        <nav className="p-3 space-y-1">
          {currentItems.map((item) => {
            const badge =
              item.path === '/communication-center' && unreadMessagesCount > 0
                ? unreadMessagesCount
                : item.path === '/platform-admin-dashboard?tab=contact' && unreadContactSubmissionsCount > 0
                  ? unreadContactSubmissionsCount
                : item.path === '/notifications' && unreadNotificationsCount > 0
                  ? unreadNotificationsCount
                  : item.badge;
            return (
            <button
              key={item.path}
              onClick={() => handleNavigation(item.path)}
              className={`group relative flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-150 ${
                isActivePath(item.path)
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-foreground hover:bg-muted hover:text-foreground'
              }`}
              aria-current={isActivePath(item.path) ? 'page' : undefined}
              title={isCollapsed ? item.label : undefined}
            >
              <Icon
                name={item.icon}
                size={20}
                className={isActivePath(item.path) ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-foreground'}
              />
              {!isCollapsed && (
                <>
                  <div className="flex-1 text-left">
                    <div className="flex items-center justify-between">
                      <span>{item.label}</span>
                      {badge ? (
                        <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-medium text-white bg-error rounded-full">
                          {badge}
                        </span>
                      ) : null}
                    </div>
                    {item.description && (
                      <span className="block text-xs text-muted-foreground mt-0.5">
                        {item.description}
                      </span>
                    )}
                  </div>
                </>
              )}
            </button>
            );
          })}
        </nav>

        {!isCollapsed && (
          <div className="p-4 mt-4 border-t border-border">
            <div className="p-3 bg-muted rounded-md">
              <div className="flex items-start gap-2 mb-2">
                <Icon name="Lightbulb" size={16} className="text-accent mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-foreground">Sfat rapid</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Folosește comenzile rapide pentru a naviga mai repede între secțiuni.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </aside>

      <aside
        className={`fixed left-0 top-16 bottom-0 z-[900] bg-card border-r border-border w-64 lg:hidden transform transition-transform duration-300 ease-out overflow-y-auto ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-label="Navigare bară laterală (mobil)"
      >
        <nav className="p-3 space-y-1">
          {currentItems.map((item) => {
            const badge =
              item.path === '/communication-center' && unreadMessagesCount > 0
                ? unreadMessagesCount
                : item.path === '/notifications' && unreadNotificationsCount > 0
                  ? unreadNotificationsCount
                  : item.badge;
            return (
            <button
              key={item.path}
              onClick={() => handleNavigation(item.path)}
              className={`group flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-150 ${
                isActivePath(item.path)
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-foreground hover:bg-muted'
              }`}
              aria-current={isActivePath(item.path) ? 'page' : undefined}
            >
              <Icon
                name={item.icon}
                size={20}
                className={isActivePath(item.path) ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-foreground'}
              />
              <div className="flex-1 text-left">
                <div className="flex items-center justify-between">
                  <span>{item.label}</span>
                  {badge ? (
                    <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-medium text-white bg-error rounded-full">
                      {badge}
                    </span>
                  ) : null}
                </div>
                {item.description && (
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    {item.description}
                  </span>
                )}
              </div>
            </button>
            );
          })}
        </nav>
      </aside>

      {isMobileOpen ? (
        <div
          className="fixed inset-0 bg-black/50 z-[850] lg:hidden animate-fade-in"
          onClick={() => setIsMobileOpen(false)}
          aria-hidden="true"
        />
      ) : null}
    </>
  );
};

export default DashboardSidebar;
